import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { errText } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Queue, Student, PaperPlaneRight, Warning, CheckCircle, Flag, CircleNotch, GraduationCap, Scales } from "@phosphor-icons/react";
import { CoachingDigest } from "../components/CoachingDigest";
import { DeadlineCalendar } from "../components/DeadlineCalendar";

const STATUS_BADGE = {
  assigned: "bg-zinc-100 text-zinc-600 border-zinc-200", in_progress: "bg-blue-50 text-blue-700 border-blue-200",
  rejected: "bg-rose-50 text-rose-700 border-rose-200", submitted_for_review: "bg-amber-50 text-amber-700 border-amber-200",
  senior_approved: "bg-indigo-50 text-indigo-700 border-indigo-200", completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

function CoachAlerts({ engagementId }) {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["coach", engagementId], queryFn: () => api.get(`/workflow/coach/${engagementId}`).then((r) => r.data) });
  const ack = useMutation({
    mutationFn: ({ lessonId, action }) => api.post("/workflow/coach/ack", { engagement_id: engagementId, lesson_id: lessonId, action }),
    onSuccess: (_, v) => { toast.success(v.action === "verified" ? "Verification recorded" : "Flagged as pending exception for your reviewer"); qc.invalidateQueries({ queryKey: ["coach", engagementId] }); },
    onError: (e) => toast.error(errText(e)),
  });
  const warnings = data?.warnings || [];
  if (!warnings.length) return null;
  return (
    <div className="mt-2 rounded-md border border-amber-200 bg-amber-50/60 p-3" data-testid={`coach-alerts-${engagementId}`}>
      <p className="text-[10px] uppercase tracking-wide text-amber-800 font-medium flex items-center gap-1 mb-2"><GraduationCap size={12} /> AI Coach — {warnings.length} pre-submission warning{warnings.length > 1 ? "s" : ""} from past reviewer notes</p>
      {warnings.map((w) => (
        <div key={w.id} data-testid={`coach-warning-${w.id}`} className="mb-2 last:mb-0">
          <p className="text-[11px] text-zinc-800 font-medium">{w.context}{w.tax_code_reference ? ` · ${w.tax_code_reference}` : ""}</p>
          <p className="text-[10px] text-zinc-600 mt-0.5">{w.mistake_rule}</p>
          {w.ack ? (
            <p className="text-[10px] mt-1 text-zinc-500 flex items-center gap-1">{w.ack === "verified" ? <><CheckCircle size={11} className="text-emerald-600" /> Verified by preparer</> : <><Flag size={11} className="text-rose-600" /> Flagged as pending exception</>}</p>
          ) : (
            <div className="flex gap-2 mt-1">
              <button data-testid={`coach-verify-${w.id}`} onClick={() => ack.mutate({ lessonId: w.id, action: "verified" })} className="text-[10px] text-emerald-700 underline hover:no-underline">Yes, I have verified</button>
              <button data-testid={`coach-flag-${w.id}`} onClick={() => ack.mutate({ lessonId: w.id, action: "exception" })} className="text-[10px] text-rose-700 underline hover:no-underline">Flag as pending exception</button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function RejectDialog({ target, onOpenChange, endpoint, refresh }) {
  const [notes, setNotes] = useState("");
  const rej = useMutation({
    mutationFn: () => api.post(`${endpoint}/${target.id}`, { action: "reject", notes }),
    onSuccess: ({ data }) => { toast.success(data.message); refresh(); onOpenChange(false); },
    onError: (e) => toast.error(errText(e)),
  });
  return (
    <Dialog open={!!target} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-heading text-sm">Reject to staff — {target?.client_name}</DialogTitle></DialogHeader>
        <p className="text-xs text-zinc-500">Your notes land on the preparer's workbench feedback cache and feed the AI coach.</p>
        <Input data-testid="reject-notes-input" placeholder="What must be fixed? (required)" value={notes} onChange={(e) => setNotes(e.target.value)} />
        <Button data-testid="reject-confirm-btn" variant="destructive" disabled={!notes.trim() || rej.isPending} onClick={() => rej.mutate()}>Reject with notes</Button>
      </DialogContent>
    </Dialog>
  );
}

export default function Workbench() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const isMgmt = ["cpa", "admin", "partner"].includes(user?.role);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectEndpoint, setRejectEndpoint] = useState("/workflow/review");
  const refresh = () => { qc.invalidateQueries({ queryKey: ["workbench"] }); qc.invalidateQueries({ queryKey: ["wf-queue"] }); qc.invalidateQueries({ queryKey: ["staff-load"] }); };

  const { data: wb } = useQuery({ queryKey: ["workbench"], queryFn: () => api.get("/workflow/workbench").then((r) => r.data) });
  const { data: q } = useQuery({ queryKey: ["wf-queue"], queryFn: () => api.get("/workflow/queue").then((r) => r.data), enabled: isMgmt });
  const { data: load } = useQuery({ queryKey: ["staff-load"], queryFn: () => api.get("/workflow/staff-load").then((r) => r.data), enabled: isMgmt });

  const autoAssign = useMutation({
    mutationFn: () => api.post("/workflow/auto-assign", { count: 3 }),
    onSuccess: ({ data }) => { toast.success(data.message); (data.assigned || []).forEach((a) => toast.info(`${a.client_name} (S=${a.priority_score}) → ${a.staff_name}`)); refresh(); },
    onError: (e) => toast.error(errText(e)),
  });
  const submit = useMutation({
    mutationFn: (id) => api.post(`/workflow/submit/${id}`, {}),
    onSuccess: () => { toast.success("Submitted for senior review"); refresh(); },
    onError: (e) => toast.error(errText(e)),
  });
  const approve = useMutation({
    mutationFn: ({ id, endpoint }) => api.post(`${endpoint}/${id}`, { action: "approve" }),
    onSuccess: ({ data }) => { toast.success(data.message); refresh(); },
    onError: (e) => toast.error(errText(e)),
  });

  const tasks = wb?.tasks || [];
  const forSenior = tasks.filter((t) => t.status === "submitted_for_review");
  const forPartner = tasks.filter((t) => t.status === "senior_approved");
  const staffCards = wb?.is_staff_view ? tasks : [];

  return (
    <div className="max-w-5xl mx-auto p-4 md:p-8 space-y-6 fade-up" data-testid="workbench-page">
      <div>
        <h1 className="font-heading text-xl text-zinc-900">{wb?.is_staff_view ? "My Workbench" : "Firm Flow — Workflow Control"}</h1>
        <p className="text-xs text-zinc-500 mt-1">{wb?.is_staff_view ? "Returns routed to you by the dynamic assignment engine, with AI coaching before you submit." : "Dynamic routing queue, multi-tier review chain, and staff load balancing."}</p>
      </div>

      {isMgmt && (
        <div className="rounded-lg border border-zinc-200 bg-white p-4" data-testid="assignment-queue-card">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-zinc-900 flex items-center gap-1.5"><Queue size={15} /> Dynamic assignment queue</h3>
            <Button data-testid="auto-assign-btn" size="sm" className="h-8 text-xs" disabled={autoAssign.isPending || !(q?.queue || []).length} onClick={() => autoAssign.mutate()}>
              {autoAssign.isPending ? <CircleNotch size={13} className="animate-spin mr-1" /> : <Scales size={13} className="mr-1" />} Auto-assign top 3
            </Button>
          </div>
          <p className="text-[10px] text-zinc-400 mb-2">S = {q?.weights?.alpha}·ClientTier + {q?.weights?.beta}·Age + {q?.weights?.gamma}·DeadlineProximity → routed to lowest-backlog preparer (argmin W)</p>
          <div className="space-y-1.5 max-h-52 overflow-y-auto">
            {(q?.queue || []).map((item) => (
              <div key={item.engagement_id} data-testid={`queue-item-${item.engagement_id}`} className="flex items-center justify-between text-xs border-b border-zinc-100 last:border-0 py-1.5">
                <span className="text-zinc-700">{item.client_name} <span className="text-zinc-400">· {item.service_type} {item.tax_year}</span></span>
                <span className="font-mono text-[10px] text-zinc-500" title={`P=${item.components.p_client} A=${item.components.a_age} D=${item.components.d_deadline}`}>S={item.priority_score} · K={item.complexity}</span>
              </div>
            ))}
            {!(q?.queue || []).length && <p className="text-xs text-zinc-400 italic">Queue is empty — every eligible return is assigned.</p>}
          </div>
          <div className="flex flex-wrap gap-2 mt-3" data-testid="staff-load-chips">
            {(load?.staff || []).map((s) => (
              <span key={s.staff_user_id} className="text-[10px] bg-zinc-50 border border-zinc-200 rounded-sm px-2 py-1">{s.name} · W={s.backlog_weight}</span>
            ))}
          </div>
        </div>
      )}

      {isMgmt && <CoachingDigest />}

      {isMgmt && ["cpa", "admin"].includes(user?.role) && (
        <div className="rounded-lg border border-zinc-200 bg-white p-4" data-testid="senior-review-card">
          <h3 className="text-sm font-medium text-zinc-900 mb-3">Awaiting senior review <span className="font-mono text-xs text-zinc-400">{forSenior.length}</span></h3>
          {forSenior.map((t) => (
            <div key={t.id} data-testid={`senior-item-${t.id}`} className="flex items-center justify-between gap-2 border-b border-zinc-100 last:border-0 py-2">
              <div><p className="text-xs text-zinc-800">{t.client_name} · {t.service_type} {t.tax_year}</p>
                <p className="text-[10px] text-zinc-400">prepared by {t.staff_name} · S={t.priority_score}</p></div>
              <div className="flex gap-2 shrink-0">
                <Button data-testid={`senior-approve-${t.id}`} size="sm" className="h-7 text-[11px]" onClick={() => approve.mutate({ id: t.id, endpoint: "/workflow/review" })}>Approve & escalate</Button>
                <Button data-testid={`senior-reject-${t.id}`} size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => { setRejectEndpoint("/workflow/review"); setRejectTarget(t); }}>Reject</Button>
              </div>
            </div>
          ))}
          {!forSenior.length && <p data-testid="senior-empty" className="text-xs text-zinc-400 italic">Nothing awaiting senior review.</p>}
        </div>
      )}

      {isMgmt && ["partner", "admin"].includes(user?.role) && (
        <div className="rounded-lg border border-zinc-200 bg-white p-4" data-testid="partner-signoff-card">
          <h3 className="text-sm font-medium text-zinc-900 mb-3">Awaiting partner sign-off <span className="font-mono text-xs text-zinc-400">{forPartner.length}</span></h3>
          {forPartner.map((t) => (
            <div key={t.id} data-testid={`partner-item-${t.id}`} className="flex items-center justify-between gap-2 border-b border-zinc-100 last:border-0 py-2">
              <div><p className="text-xs text-zinc-800">{t.client_name} · {t.service_type} {t.tax_year}</p>
                <p className="text-[10px] text-zinc-400">senior-approved by {t.senior_reviewed_by} · prepared by {t.staff_name}</p></div>
              <div className="flex gap-2 shrink-0">
                <Button data-testid={`partner-approve-${t.id}`} size="sm" className="h-7 text-[11px]" onClick={() => approve.mutate({ id: t.id, endpoint: "/workflow/signoff" })}>Final sign-off</Button>
                <Button data-testid={`partner-reject-${t.id}`} size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => { setRejectEndpoint("/workflow/signoff"); setRejectTarget(t); }}>Reject</Button>
              </div>
            </div>
          ))}
          {!forPartner.length && <p data-testid="partner-empty" className="text-xs text-zinc-400 italic">Nothing awaiting sign-off.</p>}
        </div>
      )}

      {wb?.is_staff_view ? (
        <div className="space-y-4" data-testid="staff-assignments">
          {staffCards.map((t) => (
            <div key={t.id} data-testid={`assignment-${t.id}`} className="rounded-lg border border-zinc-200 bg-white p-4">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <p className="text-sm font-medium text-zinc-900">{t.client_name} <span className="text-zinc-400 font-normal">· {t.service_type} {t.tax_year}</span></p>
                  <p className="text-[10px] text-zinc-400 mt-0.5">Priority S={t.priority_score} · Complexity K={t.complexity} · assigned {new Date(t.assigned_at).toLocaleDateString()}{t.deadline ? ` · due ${t.deadline}` : ""}</p>
                </div>
                <span className={`text-[9px] uppercase border rounded-sm px-1.5 py-0.5 shrink-0 ${STATUS_BADGE[t.status]}`}>{t.status.replace(/_/g, " ")}</span>
              </div>
              {(t.feedback || []).length > 0 && (
                <div className="mt-2 rounded-md border border-rose-200 bg-rose-50/50 p-2.5" data-testid={`feedback-cache-${t.id}`}>
                  <p className="text-[10px] uppercase tracking-wide text-rose-700 font-medium flex items-center gap-1 mb-1"><Warning size={11} /> Reviewer feedback cache</p>
                  {t.feedback.map((f) => <p key={`${f.at}-${f.by}-${f.tier}`} className="text-[11px] text-zinc-700">"{f.notes}" — <span className="text-zinc-400">{f.by} ({f.tier}), {new Date(f.at).toLocaleDateString()}</span></p>)}
                </div>
              )}
              <CoachAlerts engagementId={t.engagement_id} />
              {["assigned", "in_progress", "rejected"].includes(t.status) && (
                <Button data-testid={`submit-review-${t.id}`} size="sm" className="h-8 text-xs mt-3" disabled={submit.isPending} onClick={() => submit.mutate(t.id)}>
                  <PaperPlaneRight size={13} className="mr-1" /> Submit for senior review
                </Button>
              )}
            </div>
          ))}
          {!staffCards.length && <p className="text-sm text-zinc-400 italic">No returns assigned to you yet — the router will feed your queue.</p>}
        </div>
      ) : (
        <div className="rounded-lg border border-zinc-200 bg-white p-4" data-testid="all-assignments-card">
          <h3 className="text-sm font-medium text-zinc-900 mb-3 flex items-center gap-1.5"><Student size={15} /> All active assignments</h3>
          {tasks.map((t) => (
            <div key={t.id} className="flex items-center justify-between text-xs border-b border-zinc-100 last:border-0 py-1.5">
              <span className="text-zinc-700">{t.client_name} · {t.service_type} <span className="text-zinc-400">→ {t.staff_name}</span></span>
              <span className={`text-[9px] uppercase border rounded-sm px-1.5 py-0.5 ${STATUS_BADGE[t.status]}`}>{t.status.replace(/_/g, " ")}</span>
            </div>
          ))}
          {!tasks.length && <p className="text-xs text-zinc-400 italic">No assignments yet — use Auto-assign above.</p>}
        </div>
      )}

      {rejectTarget && <RejectDialog target={rejectTarget} endpoint={rejectEndpoint} refresh={refresh} onOpenChange={(o) => !o && setRejectTarget(null)} />}

      <DeadlineCalendar />
    </div>
  );
}
